import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminSecurityEventRow } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Spinner,
} from "../../ui";
import { Pager, Select, StaffAction, useCursorPages } from "./shared";

const KINDS = ["impossible_travel", "credential_stuffing", "account_lock"];

/** Security events (docs/phase6/P6-T04 §3) with unlock actions. */
export function SecurityEventsPage() {
  const { role } = useOutletContext<{ role: StaffRole }>();
  const canMutate = roleAtLeast(role, "admin");
  const EMPTY = { kind: "", user: "", ip: "" };
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const pages = useCursorPages();
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const query = useQuery({
    queryKey: ["admin", "security-events", filters, pages.cursor],
    queryFn: () =>
      adminApi.securityEvents({ ...filters, cursor: pages.cursor }),
  });
  const unlock = (body: { ip?: string; email?: string }) =>
    adminApi
      .unlock(body)
      .then((r) =>
        setMessage({
          kind: "success",
          text: r.ip || r.email ? "Unlocked." : "Nothing was locked.",
        }),
      )
      .catch((err) => setMessage({ kind: "error", text: errorMessage(err) }));
  const columns: ColumnDef<AdminSecurityEventRow, unknown>[] = [
    {
      id: "created_at",
      header: "When",
      cell: ({ row }) => (
        <span title={utcTooltip(row.original.created_at)}>
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: "kind",
      header: "Kind",
      cell: ({ row }) => (
        <Badge
          tone={
            row.original.kind === "impossible_travel" ? "warning" : "danger"
          }
        >
          {row.original.kind}
        </Badge>
      ),
    },
    {
      id: "user",
      header: "User",
      cell: ({ row }) =>
        row.original.user_id ? (
          <Link
            className="text-brand underline"
            to={`/admin/users/${row.original.user_id}`}
          >
            {row.original.email ?? row.original.user_id}
          </Link>
        ) : (
          (row.original.email ?? "—")
        ),
    },
    { accessorKey: "ip", header: "IP" },
    {
      id: "details",
      header: "Details",
      cell: ({ row }) => (
        <code className="text-xs">{JSON.stringify(row.original.details)}</code>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.kind === "credential_stuffing" && row.original.ip ? (
          <StaffAction
            allowed={canMutate}
            onClick={() => void unlock({ ip: row.original.ip ?? undefined })}
          >
            Unlock IP
          </StaffAction>
        ) : row.original.kind === "account_lock" && row.original.email ? (
          <StaffAction
            allowed={canMutate}
            onClick={() =>
              void unlock({ email: row.original.email ?? undefined })
            }
          >
            Unlock account
          </StaffAction>
        ) : null,
    },
  ];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Security events</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Filter security events"
      >
        <Select
          label="Kind"
          value={draft.kind}
          onChange={(kind) => setDraft({ ...draft, kind })}
          options={[
            { value: "", label: "any" },
            ...KINDS.map((k) => ({ value: k, label: k })),
          ]}
        />
        <Input
          label="User id"
          value={draft.user}
          onChange={(e) => setDraft({ ...draft, user: e.target.value })}
        />
        <Input
          label="IP"
          value={draft.ip}
          onChange={(e) => setDraft({ ...draft, ip: e.target.value })}
        />
        <div className="flex items-end">
          <Button type="submit">Filter</Button>
        </div>
      </form>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminSecurityEventRow>
          columns={columns}
          data={query.data.items}
          emptyMessage="No security events."
        />
      ) : null}
      <Pager pages={pages} nextCursor={query.data?.next_cursor} />
    </Card>
  );
}
